import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Icons from 'lucide-react';
import { Card, Button } from '../components/design-system';
import { PLAN_TEMPLATES, CATEGORY_INFO, PLAN_CATEGORIES } from '../data/planTemplates';

export const TemplateSelectionPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const categories = Object.values(CATEGORY_INFO);

  const getIcon = (iconName) => {
    const Icon = Icons[iconName];
    return Icon ? <Icon className="h-6 w-6" /> : <Icons.FileText className="h-6 w-6" />;
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
  };

  const handleStartPlan = () => {
    if (selectedTemplate) {
      navigate(`/plans/new/wizard/${selectedTemplate.id}`);
    }
  };

  const filteredTemplates = selectedCategory
    ? PLAN_TEMPLATES.filter(t => t.category === selectedCategory)
    : PLAN_TEMPLATES;

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">{t('templates.selectTitle')}</h1>
          <p className="text-gray-600 mt-1">{t('templates.selectSubtitle')}</p>
        </div>

        {/* Category Filter */}
        <div className="mb-8">
          <h2 className="text-sm font-medium text-gray-700 mb-3">{t('templates.filterByCategory')}</h2>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === null
                  ? 'bg-primary-100 text-primary-700 ring-2 ring-primary-500'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {t('templates.allCategories')}
            </button>
            {categories.map((category) => {
              const CategoryIcon = Icons[category.icon];
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-primary-100 text-primary-700 ring-2 ring-primary-500'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {CategoryIcon && <CategoryIcon className="h-4 w-4" />}
                  {t(category.nameKey)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Templates Grid */}
        <div className="grid gap-4 md:grid-cols-2 mb-8">
          {filteredTemplates.map((template) => {
            const isSelected = selectedTemplate?.id === template.id;
            const TemplateIcon = Icons[template.icon];
            const category = CATEGORY_INFO[template.category];
            
            return (
              <Card
                key={template.id}
                className={`p-5 cursor-pointer transition-all ${
                  isSelected
                    ? 'ring-2 ring-primary-500 bg-primary-50'
                    : 'hover:shadow-md hover:border-gray-300'
                }`}
                onClick={() => handleTemplateSelect(template)}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${isSelected ? 'bg-primary-100' : 'bg-gray-100'}`}>
                    {TemplateIcon && <TemplateIcon className={`h-6 w-6 ${isSelected ? 'text-primary-600' : 'text-gray-600'}`} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`font-semibold mb-1 ${isSelected ? 'text-primary-900' : 'text-gray-900'}`}>
                      {t(template.nameKey)}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {t(template.descriptionKey)}
                    </p>
                    <div className="mt-2">
                      <span className="inline-flex items-center text-xs text-gray-500">
                        {t(category.nameKey)}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Selected Template Details & Action */}
        {selectedTemplate && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg lg:left-64 lg:right-80">
            <div className="max-w-6xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-2 bg-primary-100 rounded-lg">
                  {getIcon(selectedTemplate.icon)}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{t(selectedTemplate.nameKey)}</h3>
                  <p className="text-sm text-gray-600">
                    {selectedTemplate.sections.length} {t('templates.sectionCount')} • {selectedTemplate.questions.length} {t('templates.questionCount')}
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setSelectedTemplate(null)}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleStartPlan}>
                  {t('templates.startPlan')}
                  <Icons.ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
